'use client'

import { use } from 'react'
import { ArrowLeft, ArrowRight } from '@carbon/icons-react'
import Link from 'next/link'
import { notFound } from 'next/navigation'

import { cn } from '@/lib/utils'

import {
  type Product,
  type Update,
  type UpdateType,
  VERSIONS,
  getVersion,
  getUpdatesByVersion,
  groupByProduct,
} from '../data'
import {
  HAIRLINE,
  RecordBand,
  ToggleChip,
  TYPE_META,
  UpdatesMasthead,
} from '../update-kit'

/* ─── Release page ─────────────────────────────────────────────────────────
   Same ledger as the index, one level in: the entry number hangs in the left
   margin, the change reads as a headline, and hairlines do the dividing.

   Two things here were broken rather than merely plain. Every product and
   type carried a hardcoded light-only palette (`bg-blue-50 text-blue-700
   border-blue-200`, `bg-rose-50`, …), which on the dark canvas rendered as
   pale blocks of near-black text; and the sticky jump strip was
   `bg-white/95`, a white bar across a dark page. Product colour is gone
   altogether — it was six hues of noise for information the section heading
   already carries — and type colour is now a text tone with a dark
   counterpart. ── */

const PRODUCT_ORDER: Exclude<Product, 'All'>[] = [
  'UDC',
  'HRMS',
  'Payroll',
  'Accounts',
  'LMS',
  'SchoolERP',
]

const MINOR_TYPES: Exclude<UpdateType, 'All'>[] = ['Bug Fix', 'Security']
const MAJOR_TYPES: Exclude<UpdateType, 'All'>[] = ['New Feature', 'Improvement']

/* ── Entry ── */

function UpdateEntry({
  update,
  sectionNum,
  itemNum,
}: {
  update: Update
  sectionNum: number
  itemNum: number
}) {
  const { icon: Icon, label, tone } = TYPE_META[update.type]

  return (
    <article
      id={`update-${update.id}`}
      className={cn(
        'grid scroll-mt-28 gap-x-10 gap-y-4 border-b py-8 sm:py-10',
        'sm:grid-cols-[5rem_minmax(0,1fr)]',
        HAIRLINE
      )}
    >
      <div className="sm:pt-1.5">
        <p className="font-mono text-sm tabular-nums text-muted-foreground/70">
          {sectionNum}.{itemNum}
        </p>
      </div>

      <div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <span
            className={cn(
              'inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.16em]',
              tone
            )}
          >
            <Icon className="size-3.5" />
            {label}
          </span>

          {update.major && (
            <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground/70">
              Major release
            </span>
          )}

          <span className="ml-auto text-sm tabular-nums text-muted-foreground/70">
            {update.date}
          </span>
        </div>

        <h3 className="font-heading mt-4 text-xl font-normal leading-snug text-foreground sm:text-2xl">
          {update.title}
        </h3>

        <p className="mt-3 max-w-3xl text-base leading-relaxed text-muted-foreground">
          {update.description}
        </p>

        {update.bullets.length > 0 && (
          <ul className="mt-5 max-w-3xl space-y-2.5">
            {update.bullets.map((bullet) => (
              <li
                key={bullet}
                className="flex items-start gap-3 text-[15px] leading-relaxed text-muted-foreground"
              >
                <span
                  aria-hidden
                  className="mt-2 size-1 shrink-0 rounded-full bg-emerald-600 dark:bg-primary-active"
                />
                {bullet}
              </li>
            ))}
          </ul>
        )}
      </div>
    </article>
  )
}

/* ── Product section ── */

function ProductSection({
  product,
  updates,
  sectionNum,
}: {
  product: Exclude<Product, 'All'>
  updates: Update[]
  sectionNum: number
}) {
  const major = updates.filter((u) => MAJOR_TYPES.includes(u.type))

  return (
    <section id={`section-${product}`} className="scroll-mt-28 pt-14 sm:pt-20">
      <div
        className={cn(
          'grid gap-x-10 gap-y-2 border-b pb-5 sm:grid-cols-[5rem_minmax(0,1fr)]',
          HAIRLINE
        )}
      >
        <p className="font-mono text-sm tabular-nums text-muted-foreground/70">
          {String(sectionNum).padStart(2, '0')}
        </p>
        <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
          <h2 className="font-heading text-2xl font-normal text-foreground sm:text-3xl">
            {product}
          </h2>
          <span className="text-sm text-muted-foreground">
            {updates.length} update{updates.length !== 1 ? 's' : ''}
          </span>
        </div>
      </div>

      {major.length > 0 ? (
        major.map((u, i) => (
          <UpdateEntry
            key={u.id}
            update={u}
            sectionNum={sectionNum}
            itemNum={i + 1}
          />
        ))
      ) : (
        <p
          className={cn(
            'border-b py-8 text-base text-muted-foreground',
            HAIRLINE
          )}
        >
          No major features in this section.
        </p>
      )}
    </section>
  )
}

/* ── Minor updates ── */

function MinorUpdates({ updates }: { updates: Update[] }) {
  if (updates.length === 0) return null

  return (
    <section id="minor-updates" className="scroll-mt-28 pt-14 sm:pt-20">
      <div
        className={cn(
          'grid gap-x-10 gap-y-2 border-b pb-5 sm:grid-cols-[5rem_minmax(0,1fr)]',
          HAIRLINE
        )}
      >
        <p className="font-mono text-sm tabular-nums text-muted-foreground/70">
          ···
        </p>
        <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
          <h2 className="font-heading text-2xl font-normal text-foreground sm:text-3xl">
            Fixes and patches
          </h2>
          <span className="text-sm text-muted-foreground">
            {updates.length} item{updates.length !== 1 ? 's' : ''}
          </span>
        </div>
      </div>

      {/* Two-up hairline list — the old version wrapped each row in a bordered
          card with the icon in its own boxed square. */}
      <div className="grid sm:grid-cols-2 sm:gap-x-10">
        {updates.map((u) => {
          const { label, tone } = TYPE_META[u.type]
          return (
            <div key={u.id} className={cn('border-b py-5', HAIRLINE)}>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] font-semibold uppercase tracking-[0.16em]">
                <span className={tone}>{label}</span>
                <span className="text-muted-foreground/60">{u.product}</span>
              </div>
              <p className="mt-2.5 text-base leading-snug text-foreground">
                {u.title}
              </p>
              <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
                {u.description}
              </p>
            </div>
          )
        })}
      </div>
    </section>
  )
}

/* ── Page ── */

export default function VersionDetailPage(props: {
  params: Promise<{ version: string }>
}) {
  const params = use(props.params)
  const versionSlug = decodeURIComponent(params.version)
  const version = getVersion(versionSlug)

  if (!version) notFound()

  const allUpdates = getUpdatesByVersion(versionSlug)
  const byProduct = groupByProduct(allUpdates)
  const minorUpdates = allUpdates.filter((u) => MINOR_TYPES.includes(u.type))
  const activeProducts = PRODUCT_ORDER.filter((p) => byProduct[p]?.length)

  const currentIdx = VERSIONS.findIndex((v) => v.version === versionSlug)
  const prevVersion = VERSIONS[currentIdx + 1] ?? null
  const nextVersion = VERSIONS[currentIdx - 1] ?? null

  return (
    <main className="min-h-screen bg-background">
      <UpdatesMasthead
        eyebrow={
          <>
            Release{' '}
            <span className="font-mono normal-case">{version.version}</span>
          </>
        }
        title={version.tagline}
        deck={version.highlight}
        above={
          <Link
            href="/product-updates"
            className="mb-8 inline-flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
          >
            <ArrowLeft className="size-4" />
            All releases
          </Link>
        }
      >
        <RecordBand
          fields={[
            {
              label: 'Version',
              value: <span className="font-mono">{version.version}</span>,
            },
            { label: 'Released', value: version.releaseDate },
            { label: 'Updates', value: `${allUpdates.length}` },
            {
              label: 'Products',
              /* activeProducts, not version.products: the sections and jump
                 chips below are derived from the actual updates, so the
                 masthead must promise exactly that set. The metadata list
                 once said "Payroll, LMS" on releases whose pages had no such
                 section to jump to. */
              value: activeProducts.join(', '),
            },
          ]}
        />
      </UpdatesMasthead>

      {/* Jump strip. Was `bg-white/95` — a white bar over the dark canvas. */}
      <div
        className={cn(
          'sticky top-[var(--header-h)] z-40 border-b',
          HAIRLINE,
          'bg-background/85 backdrop-blur-md dark:bg-[#09090b]/80'
        )}
      >
        <div className="container mx-auto">
          <div className="scrollbar-hide flex items-center gap-1.5 overflow-x-auto py-2.5">
            <span className="mr-1 shrink-0 text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground/70">
              Jump to
            </span>
            {activeProducts.map((p, i) => (
              <ToggleChip key={p} href={`#section-${p}`}>
                <span className="font-mono text-xs text-muted-foreground/60">
                  {String(i + 1).padStart(2, '0')}
                </span>
                {p}
                <span className="text-xs tabular-nums text-muted-foreground/60">
                  {byProduct[p]!.length}
                </span>
              </ToggleChip>
            ))}
            {minorUpdates.length > 0 && (
              <ToggleChip href="#minor-updates">
                Fixes
                <span className="text-xs tabular-nums text-muted-foreground/60">
                  {minorUpdates.length}
                </span>
              </ToggleChip>
            )}
          </div>
        </div>
      </div>

      <div className="container mx-auto pb-8">
        {activeProducts.map((product, i) => (
          <ProductSection
            key={product}
            product={product}
            updates={byProduct[product]!}
            sectionNum={i + 1}
          />
        ))}

        <MinorUpdates updates={minorUpdates} />
      </div>

      {/* ── Prev / next — one band, split by a single rule ── */}
      <div className={cn('border-t', HAIRLINE)}>
        <div className="container mx-auto">
          <div className="grid sm:grid-cols-2 sm:gap-px sm:bg-border/70">
            {prevVersion ? (
              <Link
                href={`/product-updates/${prevVersion.version}`}
                className={cn(
                  'group border-b bg-background py-8 sm:border-b-0 sm:pr-8',
                  HAIRLINE
                )}
              >
                <span className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground/70">
                  <ArrowLeft className="size-3.5 transition-transform group-hover:-translate-x-1" />
                  Previous release
                </span>
                <p className="mt-3 font-mono text-base text-foreground">
                  {prevVersion.version}
                </p>
                <p className="mt-1 text-base text-muted-foreground">
                  {prevVersion.tagline}
                </p>
              </Link>
            ) : (
              <div className="hidden bg-background sm:block" />
            )}

            {nextVersion ? (
              <Link
                href={`/product-updates/${nextVersion.version}`}
                className="group bg-background py-8 sm:pl-8 sm:text-right"
              >
                <span className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground/70">
                  Next release
                  <ArrowRight className="size-3.5 transition-transform group-hover:translate-x-1" />
                </span>
                <p className="mt-3 font-mono text-base text-foreground">
                  {nextVersion.version}
                </p>
                <p className="mt-1 text-base text-muted-foreground">
                  {nextVersion.tagline}
                </p>
              </Link>
            ) : (
              <div className="hidden bg-background sm:block" />
            )}
          </div>
        </div>
      </div>

      <div className="container mx-auto py-10 sm:py-14">
        <Link
          href="/product-updates"
          className="inline-flex items-center gap-2 text-sm font-medium text-foreground underline-offset-4 hover:underline"
        >
          <ArrowLeft className="size-4" />
          Back to all releases
        </Link>
      </div>
    </main>
  )
}
